export interface SanitizeEmailHtmlOptions {
  allowRemoteImages?: boolean;
  maxInlineDataUriLength?: number;
}

const REMOVE_TAGS = new Set([
  'script',
  'iframe',
  'object',
  'embed',
  'link',
  'meta',
  'base',
  'svg',
  'math',
  'style'
]);

const UNWRAP_TAGS = new Set([
  'form',
  'button',
  'input',
  'select',
  'textarea',
  'option'
]);

const URL_ATTRS = new Set([
  'href',
  'src',
  'xlink:href',
  'action',
  'formaction',
  'poster',
  'background'
]);

function unwrapElement(element: Element) {
  const parent = element.parentNode;
  if (!parent) return;
  while (element.firstChild) {
    parent.insertBefore(element.firstChild, element);
  }
  parent.removeChild(element);
}

function isDangerousStyle(value: string) {
  const lower = value.toLowerCase();
  const scriptScheme = `java${String.fromCharCode(115)}cript:`;
  return /(?:url\s*\(|@import|expression\s*\(|behavior\s*:|-moz-binding)/i.test(value)
    || lower.includes(scriptScheme)
    || lower.includes('vbscript:');
}

function isRemoteUrl(value: string) {
  return /^(?:https?:)?\/\//i.test(value.trim());
}

function isSafeDataImage(value: string, maxInlineDataUriLength: number) {
  const trimmed = value.trim();
  return /^data:image\/(?:png|gif|jpe?g|webp);base64,/i.test(trimmed)
    && trimmed.length <= maxInlineDataUriLength;
}

function sanitizeUrlAttr(element: Element, attrName: string, value: string, options: Required<SanitizeEmailHtmlOptions>) {
  const tagName = element.tagName.toLowerCase();
  const trimmed = value.trim();
  const normalized = Array.from(trimmed)
    .filter(char => {
      const code = char.charCodeAt(0);
      return code > 32 && code !== 127;
    })
    .join('')
    .toLowerCase();
  const scriptScheme = `java${String.fromCharCode(115)}cript:`;

  if (!trimmed) {
    element.removeAttribute(attrName);
    return;
  }

  if (
    normalized.startsWith(scriptScheme) ||
    normalized.startsWith('vbscript:') ||
    normalized.startsWith('file:') ||
    normalized.startsWith('blob:')
  ) {
    element.removeAttribute(attrName);
    return;
  }

  if (tagName === 'img' && attrName.toLowerCase() === 'src') {
    if (isRemoteUrl(trimmed) && !options.allowRemoteImages) {
      element.setAttribute('src', '');
      element.setAttribute('data-remote-image-blocked', '1');
      return;
    }

    if (normalized.startsWith('data:') && !isSafeDataImage(trimmed, options.maxInlineDataUriLength)) {
      element.setAttribute('src', '');
      element.setAttribute('data-inline-image-removed', '1');
      return;
    }
  } else if (normalized.startsWith('data:')) {
    element.removeAttribute(attrName);
  }
}

function sanitizeElement(element: Element, options: Required<SanitizeEmailHtmlOptions>) {
  const tagName = element.tagName.toLowerCase();

  if (REMOVE_TAGS.has(tagName)) {
    element.remove();
    return;
  }

  if (UNWRAP_TAGS.has(tagName)) {
    unwrapElement(element);
    return;
  }

  Array.from(element.attributes).forEach(attr => {
    const attrName = attr.name;
    const lowerName = attrName.toLowerCase();
    const value = attr.value || '';

    if (
      lowerName.startsWith('on') ||
      lowerName === 'srcdoc' ||
      lowerName === 'ping' ||
      lowerName === 'http-equiv'
    ) {
      element.removeAttribute(attrName);
      return;
    }

    if (lowerName === 'style' && isDangerousStyle(value)) {
      element.removeAttribute(attrName);
      return;
    }

    if (URL_ATTRS.has(lowerName)) {
      sanitizeUrlAttr(element, attrName, value, options);
    }
  });

  if (tagName === 'a') {
    element.setAttribute('rel', 'noopener noreferrer nofollow');
    element.setAttribute('target', '_blank');
  }

  if (tagName === 'img') {
    if (!element.hasAttribute('loading')) element.setAttribute('loading', 'lazy');
    if (!element.hasAttribute('decoding')) element.setAttribute('decoding', 'async');
  }
}

export function sanitizeEmailHtml(html: string, opts: SanitizeEmailHtmlOptions = {}) {
  if (!html) return '';

  const options: Required<SanitizeEmailHtmlOptions> = {
    allowRemoteImages: Boolean(opts.allowRemoteImages),
    maxInlineDataUriLength: Math.max(0, Number(opts.maxInlineDataUriLength ?? 4000) || 4000)
  };

  const parser = new DOMParser();
  const doc = parser.parseFromString(String(html), 'text/html');

  Array.from(doc.querySelectorAll('*')).forEach(element => {
    if (element.isConnected) {
      sanitizeElement(element, options);
    }
  });

  return doc.body.innerHTML;
}
